import type { FC } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { IconButton, Placeholder, Button } from '@/shared/ui/primitives';

import { useSearchBarState, useTelegramBackButton } from '@/shared/hooks';

import { Page } from '@/app/Page';
import { ActionSection, QueryBoundary, SearchBar } from '@/shared/ui';
import { searchByName } from '@/shared/lib/search';
import { AddIcon } from '@/shared/ui/icons/AddIcon';
import type { PassType } from '@/types';
import {
  buildPassTypeFromCreate,
  PassTemplateListItem,
  PassTemplateModal,
  passTypesQueryOptions,
  templateDraftToCreateRequest,
  templateDraftToUpdateRequest,
  useCreatePassType,
  useDeletePassType,
  useUpdatePassType,
} from '@/features/pass-types';
import { TEST_IDS } from '@/testIds';
import { type PassEditorLocationState } from '@/navigation/paths';
import styles from './index-page/ClientsList.module.css';

export const PassEditorPage: FC = () => {
  return (
    <Page back={false}>
      <QueryBoundary>
        <PassEditorPageContent />
      </QueryBoundary>
    </Page>
  );
};

const PassEditorPageContent: FC = () => {
  const { data: templates } = useSuspenseQuery(passTypesQueryOptions.list());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PassType | undefined>();
  const {
    searchQuery,
    setSearchQuery,
    isSearchVisible,
    openSearch,
    closeSearch,
    searchTriggerRef,
  } = useSearchBarState();
  const location = useLocation();
  const normalizedState = (location.state as PassEditorLocationState | null) ?? null;
  const navigate = useNavigate();
  const openModal = normalizedState?.openModal;
  const returnTo = normalizedState?.returnTo;

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingTemplate(undefined);
  }, []);

  const createPassTypeMutation = useCreatePassType({
    onSuccess: (data, variables) => {
      if (returnTo) {
        handleCloseModal();
        void navigate('.', { replace: true, state: {} });

        const createdTemplate = buildPassTypeFromCreate(data.id, variables);

        void navigate(returnTo, {
          replace: true,
          state: { createdTemplate },
        });
        return;
      }
      handleCloseModal();
    },
  });
  const updatePassTypeMutation = useUpdatePassType({
    onSuccess: () => {
      handleCloseModal();
    },
  });
  const deletePassTypeMutation = useDeletePassType({
    onSuccess: () => {
      handleCloseModal();
    },
  });

  const filteredTemplates = useMemo(
    () => searchByName(templates, searchQuery),
    [templates, searchQuery]
  );

  const handleAddTemplate = () => {
    setEditingTemplate(undefined);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: PassType) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleDeleteTemplate = async (id: string, name?: string): Promise<void> => {
    await deletePassTypeMutation.mutateAsync({ id, name });
  };

  const handleSaveTemplate = async (templateData: Omit<PassType, 'id'>): Promise<void> => {
    if (editingTemplate) {
      await updatePassTypeMutation.mutateAsync({
        id: editingTemplate.id,
        data: templateDraftToUpdateRequest(templateData),
      });
    } else {
      await createPassTypeMutation.mutateAsync(templateDraftToCreateRequest(templateData));
    }
  };

  useEffect(() => {
    if (openModal) {
      setIsModalOpen(true);

      void navigate('.', {
        replace: true,
        state: {
          returnTo,
        },
      });
    }
  }, [navigate, openModal, returnTo]);

  const backButtonVisible = isModalOpen || isSearchVisible;
  const handleBack = useCallback(() => {
    if (isModalOpen) {
      handleCloseModal();
      return;
    }
    if (isSearchVisible) {
      closeSearch();
    }
  }, [isModalOpen, isSearchVisible, handleCloseModal, closeSearch]);

  useTelegramBackButton({ visible: backButtonVisible, onBack: handleBack });

  return (
    <>
      <ActionSection
        title="Templates"
        isActive={isSearchVisible}
        inactiveActions={
          <>
            <IconButton
              size="md"
              variant="ghost"
              aria-label="Add pass template"
              onClick={handleAddTemplate}
              data-testid={TEST_IDS.addTemplateButton}
            >
              <AddIcon />
            </IconButton>
            <SearchBar
              triggerRef={searchTriggerRef}
              isVisible={isSearchVisible}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onClose={closeSearch}
              onOpen={openSearch}
            />
          </>
        }
        activeActions={
          <SearchBar
            triggerRef={searchTriggerRef}
            isVisible={isSearchVisible}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onClose={closeSearch}
            onOpen={openSearch}
          />
        }
      >
        {filteredTemplates.length === 0 ? (
          <Placeholder
            title={searchQuery ? 'No templates found' : 'No templates yet'}
            description={
              searchQuery
                ? 'Try a different name or clear your search.'
                : 'Reuse templates when adding passes to clients.'
            }
            action={
              <Button
                variant="primary"
                size="lg"
                onClick={searchQuery ? closeSearch : handleAddTemplate}
              >
                {searchQuery ? 'Clear search' : 'Create template'}
              </Button>
            }
          />
        ) : (
          <div className={styles.list}>
            {filteredTemplates.map((template) => (
              <PassTemplateListItem
                key={template.id}
                template={template}
                onEdit={handleEditTemplate}
              />
            ))}
          </div>
        )}
      </ActionSection>

      <PassTemplateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTemplate}
        onDelete={(id) => handleDeleteTemplate(id, editingTemplate?.name)}
        editTemplate={editingTemplate}
        isSaving={
          createPassTypeMutation.isPending ||
          updatePassTypeMutation.isPending ||
          deletePassTypeMutation.isPending
        }
      />
    </>
  );
};
